import { Layer, LayerDocumentUIdsSet } from '../../store/cop/models';
import { LayerColorsService } from '../layer-colors.service';
import { ClusterStats, MarkerClusterer, Cluster } from '@googlemaps/markerclusterer';
import { MapEventsService } from '../../services/map-events.service';
import { ClusterChangedEvent, ClusterClickEvent } from '../../services/cluster.models';
import { v4 as uuidv4 } from 'uuid';
import { MapLayerClusterService } from '../cluster.service';
import { Dataset } from '@app/modules/core/store/models/dataset.model';
import { PopupService } from '../popups';

const MIXED_STATE_COLOR = '#000000';

export { Layer, MapEventsService, MapLayerClusterService };

interface MarkerStateNumbers {
  scaledSize: {
    width: number;
    height: number;
  };
  anchor: {
    x: number;
    y: number;
  };
}

export interface MarkerSizeAndAnchor {
  normal: MarkerStateNumbers;
  selected: MarkerStateNumbers;
}

export interface PopupOffset {
  x?: number;
  y?: number;
}

export type SvgRenderer = (fillColor: string, borderColor: string) => string;

export abstract class ClusterRenderer {
  layerFillColor: string;
  layerBorderColor: string;
  clusterer: MarkerClusterer;
  isWorkflowSection: boolean;
  normalSvgUrl: string;
  selectedSvgUrl: string;
  normalSvgRendererFunc: SvgRenderer;
  selectedSvgRendererFunc: SvgRenderer;
  markerSizeAndAnchor: MarkerSizeAndAnchor = {
    normal: {
      scaledSize: { width: 45, height: 45 },
      anchor: { x: 22, y: 22 }
    },
    selected: {
      scaledSize: { width: 55, height: 55 },
      anchor: { x: 27, y: 27 }
    }
  };
  includeMarkerDataForPopup: boolean = false;
  popupOffset: PopupOffset = { x: 1, y: 65 };
  canShowPopup: boolean;
  clickable = true;

  constructor(
    protected layer: Layer,
    protected dataset: Dataset,
    protected mapEventsService: MapEventsService,
    protected clusterService: MapLayerClusterService,
    protected popup: PopupService,
    protected isWorkflowLayerSection: boolean,
    protected normalSvgRenderer?: SvgRenderer,
    protected selectedSvgRenderer?: SvgRenderer
  ) {
    this.layerFillColor = LayerColorsService.getLayerFillColor(this.layer.color);
    this.layerBorderColor = LayerColorsService.getLayerBorderColorFromFillColor(this.layerFillColor);
    this.normalSvgUrl = this.normalSvgRenderer(this.layerFillColor, this.layerBorderColor) ?? '';
    this.selectedSvgUrl = this.selectedSvgRenderer(this.layerFillColor, this.layerBorderColor) ?? '';
    this.isWorkflowSection = isWorkflowLayerSection;
    this.normalSvgRendererFunc = this.normalSvgRenderer;
    this.selectedSvgRendererFunc = this.selectedSvgRenderer;
    this.canShowPopup = this.popup?.isEnabledForLayer(this.layer);
  }

  protected getClusterMarker(cluster: Cluster): google.maps.Marker {
    const { scaledSize, anchor } = this.markerSizeAndAnchor.normal;

    // create marker using svg icon
    const marker = new google.maps.Marker({
      visible: cluster.count > 0,
      position: cluster.position,
      icon: {
        url: this.normalSvgUrl,
        scaledSize: new google.maps.Size(scaledSize.width, scaledSize.height),
        anchor: new google.maps.Point(anchor.x, anchor.y)
      },
      label: {
        text: String(cluster.count),
        color: this.layerBorderColor,
        fontSize: '14px',
        fontWeight: '600'
      },
      // adjust zIndex to be above other markers
      zIndex: Number(google.maps.Marker.MAX_ZINDEX) + cluster.count
    });

    return marker;
  }

  public render(cluster: Cluster, stats: ClusterStats): google.maps.Marker {
    const marker = this.getClusterMarker(cluster);

    marker.setClickable(this.clickable);

    const id = uuidv4();
    marker['__data'] = {
      ...marker['__data'],
      id,
      isSelected: false,
      fillColor: this.layerFillColor
    };

    if (this.isWorkflowSection === true) {
      const commonColor = this.getMarkersCommonColor(cluster);
      if (!!commonColor) {
        if (marker['__data'].fillColor !== commonColor) {
          marker['__data'].fillColor = commonColor;
          if (marker['__data'].isSelected === true) {
            this.renderSelected(marker);
          } else {
            this.renderNormal(marker);
          }
        }
      }
    }
    this.mapEventsService.onClusterChangedNotify(this.getChangeData(marker['__data'].id, cluster));

    marker.addListener('dblclick', () => {
      this.mapEventsService.onClusterDoubleClick(this.getClickEventData(marker['__data'].id, cluster));

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    marker.addListener('click', () => {
      this.mapEventsService.onClusterClick(this.getClickEventData(marker['__data'].id, cluster));

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    marker.addListener('mouseover', event => {
      this.renderSelected(marker);

      if (this.canShowPopup) {
        this.popup.open({
          position: marker.getPosition(),
          layer: this.layer,
          dataset: this.dataset,
          offsetY: this.popupOffset.y,
          offsetX: this.popupOffset.x,
          isCluster: true,
          markerData: this.includeMarkerDataForPopup ? marker['__data'] : null,
          offsetBelow: Boolean(marker['__data']?.offsetBelow) ? marker['__data'].offsetBelow : event.domEvent.target.clientHeight
        });
      }
    });

    marker.addListener('mouseout', () => {
      this.renderNormal(marker);

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    this.clusterService.registerClusterMarker(id, cluster, marker);

    return marker;
  }

  public renderNormal(marker: google.maps.Marker) {
    const { normal, selected } = this.markerSizeAndAnchor;

    let normalSvgUrl = this.normalSvgRendererFunc(marker['__data'].fillColor, this.layerBorderColor) ?? '';
    let selectedSvgUrl = this.selectedSvgRendererFunc(marker['__data'].fillColor, this.layerBorderColor) ?? '';

    if (marker['__data'].isSelected === true) {
      marker.setIcon({
        url: selectedSvgUrl,
        scaledSize: new google.maps.Size(selected.scaledSize.width, selected.scaledSize.height),
        anchor: new google.maps.Point(selected.anchor.x, selected.anchor.y)
      });
    } else {
      marker.setIcon({
        url: normalSvgUrl,
        scaledSize: new google.maps.Size(normal.scaledSize.width, normal.scaledSize.height),
        anchor: new google.maps.Point(normal.anchor.x, normal.anchor.y)
      });
    }
  }

  public renderSelected(marker: google.maps.Marker) {
    const { scaledSize, anchor } = this.markerSizeAndAnchor.selected;

    let normalSvgUrl = this.normalSvgRendererFunc(marker['__data'].fillColor, this.layerBorderColor) ?? '';
    let selectedSvgUrl = this.selectedSvgRendererFunc(marker['__data'].fillColor, this.layerBorderColor) ?? '';

    marker.setIcon({
      url: selectedSvgUrl,
      scaledSize: new google.maps.Size(scaledSize.width, scaledSize.height),
      anchor: new google.maps.Point(anchor.x, anchor.y)
    });
  }

  protected getClickEventData(clusterId: string, cluster: Cluster): ClusterClickEvent {
    const layerDocuments: LayerDocumentUIdsSet = {};

    cluster.markers.forEach(marker => {
      const layerUId = marker['__data'].layerUId;
      const documentUId = marker['__data'].documentUId;

      if (!!!layerDocuments[layerUId]) {
        layerDocuments[layerUId] = [];
      }
      layerDocuments[layerUId].push(documentUId);
    });

    return {
      layerDocuments: layerDocuments,
      cluster: cluster,
      clusterId
    };
  }

  private getMarkersCommonColor(cluster: Cluster): string {
    let commonColor = null;

    for (let marker of cluster.markers) {
      const fillColor = marker['__data'].fillColor;
      if (commonColor === null) {
        commonColor = fillColor;
      } else {
        if (fillColor !== commonColor) {
          return MIXED_STATE_COLOR;
        } else {
          commonColor = fillColor;
        }
      }
    }

    return commonColor;
  }

  protected getChangeData(clusterId: string, cluster: Cluster): ClusterChangedEvent {
    const layerDocuments: LayerDocumentUIdsSet = {};

    cluster?.markers?.forEach(marker => {
      const layerUId = marker['__data'].layerUId;
      const documentUId = marker['__data'].documentUId;

      if (!!!layerDocuments[layerUId]) {
        layerDocuments[layerUId] = [];
      }
      layerDocuments[layerUId].push(documentUId);
    });

    return {
      layerDocuments: layerDocuments,
      position: cluster.position,
      clusterId
    };
  }
}
